import React, {
  useEffect, useMemo, useState,
} from 'react';

import CheckRounded from '@mui/icons-material/CheckRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import DeleteRounded from '@mui/icons-material/DeleteRounded';
import DownloadRounded from '@mui/icons-material/DownloadRounded';
import ReplayRounded from '@mui/icons-material/ReplayRounded';
import {
  Grid, IconButton, Stack, Typography,
} from '@mui/material';

import Button from '../../../components/Button';
import ContentCard from '../../../components/ContentCard';
import Modal from '../../../components/Modal';
import SkeletonStack from '../../../components/SkeletonStack';
import {
  useApproveExamMarkingRequestMutation,
  useDeleteExamMarkingRequestMutation,
  useGetExamMarkingRequestQuery,
  useLazyGetExamMarkingRequestFileUrlQuery,
  useUnapproveExamMarkingRequestMutation,
} from '../../../services/exams';
import { ExamRequestStatus } from '../../../types/common';
import useDownload from '../../../utils/react/hooks/useDownload';
import useError from '../../../utils/react/hooks/useError';
import useSuccess from '../../../utils/react/hooks/useSuccess';
import RequestDetailsCard from '../RequestDetailsCard';
import RequestExamCard from '../RequestExamCard';
import RequestMarkerCard from '../RequestMarkerCard';
import RequestStudentCard from '../RequestStudentCard';

interface RequestDetailsProps {
  isAutoDrop?: boolean,
  requestId?: string,
  onClose: () => void,
}

const RequestDetails = ({ isAutoDrop, requestId, onClose }: RequestDetailsProps) => {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const { data: request, isLoading: isRequestLoading } = useGetExamMarkingRequestQuery({
    requestId: requestId || '',
  }, { skip: !requestId });

  const [getDownloadUrl, {
    data: downloadUrlData,
    isError: isDownloadUrlError,
    error: downloadUrlError,
  }] = useLazyGetExamMarkingRequestFileUrlQuery();

  const [approveMarkingRequest, {
    isLoading: isApproveMarkingRequestLoading,
    isSuccess: isApproveMarkingRequestSuccess,
    isError: isApproveMarkingRequestError,
    error: approveMarkingRequestError,
  }] = useApproveExamMarkingRequestMutation();
  const [unapproveMarkingRequest, {
    isLoading: isUnapproveMarkingRequestLoading,
    isSuccess: isUnapproveMarkingRequestSuccess,
    isError: isUnapproveMarkingRequestError,
    error: unapproveMarkingRequestError,
  }] = useUnapproveExamMarkingRequestMutation();
  const [deleteMarkingRequest, {
    isLoading: isDeleteMarkingRequestLoading,
    isSuccess: isDeleteMarkingRequestSuccess,
    isError: isDeleteMarkingRequestError,
    error: deleteMarkingRequestError,
  }] = useDeleteExamMarkingRequestMutation();

  const approveButtonLabel = useMemo(() => (
    isAutoDrop ? 'Approve & Drop' : 'Approve & Assign'
  ), [isAutoDrop]);

  useEffect(() => {
    if (isDeleteMarkingRequestSuccess) {
      onClose();
      setDeleteModalOpen(false);
    }
  }, [isDeleteMarkingRequestSuccess]);

  useEffect(() => {
    if (isDeleteMarkingRequestError) {
      setDeleteModalOpen(false);
    }
  }, [isDeleteMarkingRequestError]);

  useEffect(() => {
    if (isApproveMarkingRequestSuccess) {
      onClose();
    }
  }, [isApproveMarkingRequestSuccess]);

  useDownload(downloadUrlData?.data);

  useError(isDownloadUrlError, downloadUrlError);
  useError(isDeleteMarkingRequestError, deleteMarkingRequestError);
  useError(isApproveMarkingRequestError, approveMarkingRequestError);
  useError(isUnapproveMarkingRequestError, unapproveMarkingRequestError);

  useSuccess(isDeleteMarkingRequestSuccess, 'Marking request deleted');
  useSuccess(isApproveMarkingRequestSuccess, 'Marking request approved');
  useSuccess(isUnapproveMarkingRequestSuccess, 'Marking request unapproved');

  if (isRequestLoading) {
    return (
      <SkeletonStack />
    );
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
        >
          <IconButton onClick={onClose}>
            <CloseRounded />
          </IconButton>
          <Typography variant="h6">
            Request Details
          </Typography>
        </Stack>
      </Grid>
      <Grid item xs={12}>
        <RequestDetailsCard
          request={request?.data}
        />
      </Grid>
      <Grid item xs={12}>
        <RequestStudentCard
          requestId={request?.data.id || ''}
          studentId={request?.data.student}
        />
      </Grid>
      <Grid item xs={12}>
        <RequestExamCard
          requestId={request?.data.id || ''}
          exam={request?.data.examSubject
            && request?.data.examId
            && request?.data.examLabel ? {
              subject: request.data.examSubject,
              examId: request.data.examId,
              label: request.data.examLabel,
            } : undefined}
        />
      </Grid>
      <Grid item xs={12}>
        <RequestMarkerCard
          requestId={requestId || ''}
          marker={(request?.data.marker && request?.data.markerName) ? {
            staffId: request.data.marker,
            name: request.data.markerName,
          } : undefined}
        />
      </Grid>
      <Grid item xs={12}>
        <ContentCard title="Actions" variant="outlined">
          <Stack direction="row" spacing={1}>
            {request?.data.status === ExamRequestStatus.PendingApproval ? (
              <Button
                color="success"
                size="medium"
                onClick={() => approveMarkingRequest({
                  requestId: requestId || '',
                  autoDrop: isAutoDrop || false,
                })}
                startIcon={(<CheckRounded />)}
                loading={isApproveMarkingRequestLoading}
                disabled={!request?.data.student
                || !request?.data.examId
                || !request?.data.dueDatetime}
              >
                {approveButtonLabel}
              </Button>
            ) : (
              <Button
                color="error"
                size="medium"
                onClick={() => unapproveMarkingRequest({
                  requestId: requestId || '',
                })}
                startIcon={(<ReplayRounded />)}
                loading={isUnapproveMarkingRequestLoading}
              >
                Unapprove
              </Button>
            )}
            <IconButton
              title="Download Exam Paper"
              color="info"
              onClick={async () => getDownloadUrl({
                requestId: requestId || '',
              })}
            >
              <DownloadRounded />
            </IconButton>
            <IconButton
              title="Delete Exam Request"
              color="error"
              onClick={() => setDeleteModalOpen(true)}
            >
              <DeleteRounded />
            </IconButton>
          </Stack>
        </ContentCard>
      </Grid>
      <Modal
        open={deleteModalOpen}
        handleClose={() => setDeleteModalOpen(false)}
        title="Are you sure?"
        description="This will permamently delete this request."
        buttons={[
          {
            label: 'Delete',
            onClick: () => deleteMarkingRequest({
              requestId: requestId || '',
            }),
            color: 'error',
            loading: isDeleteMarkingRequestLoading,
          },
        ]}
      />
    </Grid>
  );
};

export default RequestDetails;
